import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewTag,
  fetchTagRegexes,
  setSelectedTagRegexesSet,
  updateTagRegexesAction,
  addNewCategoryGroup,
} from "../../../../redux/reducers/TagRegexes";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { ReduxState } from "../../../../redux";
import { flatten, groupBy } from "lodash";
import {
  Button,
  Row,
  Col,
  Modal,
  FormControl,
  InputGroup,
  Form,
  Dropdown,
} from "react-bootstrap";
import {
  deleteTagRegexes,
  deleteTagRegexesSet,
  resetEditedCategories,
  resetEditedCategoriesTagLevels,
} from "../../../../docuclipper/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCustomUi } from "../../../Docuclipper/ConfirmAlert/ConfirmAlert";
import { confirmAlert } from "react-confirm-alert";
import CategoryTable from "./CategoryTable";
import { TagCategoryRule } from "src/docuclipper/DocuclipperTypes";
import {
  handleAddNewKeyword,
  extractKeywordsFromJsonRules,
} from "src/utils/categoryManagement";
import { getAdminTagRegexes } from "src/docuclipper/api";
import AlertBanner from "src/views/Docuclipper/AlertBanner/AlertBanner";

import { useHistory } from "react-router-dom";
import { createAlert } from "src/redux/reducers/Alerts";
import { categorizeActionAsync } from "../../../../redux/reducers/CategoriesAsync";

export const TagRegexes: React.FC = () => {
  const DEFAULT_SYSTEM_GROUP = "c89e2ac11db6184500f0933279a407b3";
  const [existingTag, setExistingTag] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [showAddNewRow, setShowAddNewRow] = React.useState(false);
  const [filterText, setFilterText] = React.useState("");
  const [showNewGroup, setShowNewGroup] = React.useState(false);
  const [groupName, setGroupName] = React.useState("");
  const [changesMade, setChangesMade] = React.useState(false);
  const [systemKeywords, setSystemKeywords] = React.useState<
    Record<string, string[]>
  >({});

  const { tagRegexes, tagRegexesSets, selectedTagRegexesSet } = useSelector(
    (state: ReduxState) => state.TagRegexes,
  );

  const dispatch = useDispatch();
  React.useEffect(() => {
    dispatch(fetchTagRegexes("categorize"));
  }, []);
  const lsKey = "docuclipper.ui.selectedCategoryGroup";
  React.useEffect(() => {
    // Only run when nothing is selected but we have sets (e.g. initial load or after refetch)
    if (!selectedTagRegexesSet && tagRegexesSets.length > 0) {
      const savedSelection = localStorage.getItem(lsKey);
      const savedExistsInList = savedSelection
        ? tagRegexesSets.some((s) => s.name === savedSelection)
        : false;

      if (savedExistsInList) {
        // After first use: use saved selection from localStorage
        dispatch(setSelectedTagRegexesSet(savedSelection!));
      } else {
        // First use (no localStorage) or saved set was deleted: always pick something so we never have nothing selected
        const hasDefaultGroup = tagRegexesSets.some(
          (set) => set.name === DEFAULT_SYSTEM_GROUP,
        );
        const nameToSelect = hasDefaultGroup
          ? DEFAULT_SYSTEM_GROUP
          : tagRegexesSets[0].name;
        dispatch(setSelectedTagRegexesSet(nameToSelect));
        localStorage.setItem(lsKey, nameToSelect);
      }
    }
  }, [tagRegexesSets, selectedTagRegexesSet]);

  React.useEffect(() => {
    if (
      selectedTagRegexesSet &&
      selectedTagRegexesSet.name === DEFAULT_SYSTEM_GROUP
    ) {
      getAdminTagRegexes("categorize")
        .then(({ tagRegexes: adminTagRegexes }) => {
          const keywordsMap: Record<string, string[]> = {};
          (adminTagRegexes || []).forEach((tag) => {
            let positiveKeywords: string[] = [];
            if (tag?.jsonRules) {
              const extracted = extractKeywordsFromJsonRules(
                typeof tag.jsonRules === "string"
                  ? tag.jsonRules
                  : JSON.stringify(tag.jsonRules),
              );
              positiveKeywords = extracted.positiveKeywords || [];
            } else if (tag?.regexes) {
              try {
                positiveKeywords = JSON.parse(tag.regexes) || [];
              } catch (error) {
                positiveKeywords = [];
              }
            }

            if (positiveKeywords.length > 0) {
              keywordsMap[tag.tag] = positiveKeywords;
            }
          });
          setSystemKeywords(keywordsMap);
        })
        .catch(() => {
          setSystemKeywords({});
        });
    } else {
      setSystemKeywords({});
    }
  }, [selectedTagRegexesSet]);

  const names = Object.keys(groupBy(tagRegexesSets, "name"));
  const selectedTagIds = !selectedTagRegexesSet
    ? []
    : tagRegexesSets
        .filter((t) => t.name === selectedTagRegexesSet.name)
        .map((x) => x.tagRegexesId);

  const allTagIds = flatten(tagRegexesSets.map((x) => x.tagRegexesId));

  const getTagName = (id) => {
    const matches = tagRegexes.filter((x) => x.id === id);
    if (matches.length > 0) {
      return matches[0].tag;
    }
    return "default";
  };

  const getTagLabel = (id) => {
    const matches = tagRegexes.filter((x) => x.id === id);
    if (matches.length > 0) {
      return matches[0]?.tagLabel ? `(${matches[0]?.tagLabel})` : "";
    }
    return "";
  };

  const existingCategoriesOptions = allTagIds
    .filter((x) => !selectedTagIds.includes(x))
    .map((x) => {
      const setName =
        tagRegexesSets.find((set) => set.tagRegexesId.includes(x))?.name ||
        "unknown";
      return {
        label: `${setName} - ${getTagName(x)} ${getTagLabel(x)}`,
        value: x,
      };
    });

  const hideStr = (x: string) =>
    x === "c89e2ac11db6184500f0933279a407b3" ? "default" : x;

  const toggle = () => {
    // If closing modal and changes were made, trigger categorization
    if (show && changesMade) {
      setShow(false);
      setChangesMade(false);

      // Trigger categorization after a short delay
      setTimeout(() => {
        dispatch(categorizeActionAsync({ l1, l2 }));
        dispatch(
          createAlert({
            message: "Re-categorizing transactions with updated rules...",
            timeout: 3000,
            type: "info",
          }),
        );
      }, 300);
    } else {
      setShow(!show);
    }
  };

  const isDefault = selectedTagRegexesSet
    ? selectedTagRegexesSet.name === "c89e2ac11db6184500f0933279a407b3"
    : false;

  const onAddNewKeyword = (
    tagId: string,
    keyword: string,
    isPositive: boolean,
  ) => {
    handleAddNewKeyword(dispatch, tagRegexes, tagId, keyword, isPositive);
    setChangesMade(true);
  };

  const handleAddCategory = (
    tag: string,
    tagLabel: string,
    keywords: string[],
    negativeKeywords: string[],
  ) => {
    if (selectedTagRegexesSet) {
      const rule: TagCategoryRule = {
        keywords: keywords,
        negativeKeywords: negativeKeywords,
        tag: tag,
      };
      dispatch(
        addNewTag(selectedTagRegexesSet.name, tag, tagLabel, "[]", rule),
      );
      setShowAddNewRow(false);
      setChangesMade(true);
    }
  };

  const onRemoveKeyword = (
    tagId: string,
    keyword: string,
    isPositive: boolean,
  ) => {
    const tagToUpdate = tagRegexes.find((t) => t.id === tagId);
    if (tagToUpdate) {
      let positiveKeywords = [];
      let negativeKeywords = [];

      if (tagToUpdate.jsonRules) {
        const keywords = extractKeywordsFromJsonRules(tagToUpdate.jsonRules);
        positiveKeywords = keywords.positiveKeywords;
        negativeKeywords = keywords.negativeKeywords;
      } else if (tagToUpdate.regexes) {
        positiveKeywords = JSON.parse(tagToUpdate.regexes);
      }

      const updatedPositiveKeywords = isPositive
        ? positiveKeywords.filter((k) => k !== keyword)
        : positiveKeywords;
      const updatedNegativeKeywords = isPositive
        ? negativeKeywords
        : negativeKeywords.filter((k) => k !== keyword);
      const updatedRule = {
        keywords: updatedPositiveKeywords,
        negativeKeywords: updatedNegativeKeywords,
        tag: tagToUpdate.tag,
      };

      dispatch(
        updateTagRegexesAction(
          tagId,
          tagToUpdate.tag,
          tagToUpdate.tagLabel,
          JSON.stringify(updatedRule),
          updatedRule,
        ),
      );
      setChangesMade(true);
    }
  };

  const onDeleteTag = (id) => {
    deleteTagRegexes(id)
      .then(() => {
        dispatch(fetchTagRegexes("categorize"));
        setChangesMade(true);
      })
      .catch((err) =>
        dispatch(
          createAlert({
            message: err.message || "Error deleting. Please try again.",
            timeout: 0,
            type: "error",
          }),
        ),
      );
  };

  const onSaveTag = (
    tagId: string,
    newTag: string,
    newTagLabel: string,
    regexes: string,
    jsonRules: any,
  ) => {
    dispatch(
      updateTagRegexesAction(tagId, newTag, newTagLabel, regexes, jsonRules),
    );
    setChangesMade(true);
  };

  const onAddExistingCategoryToGroup = () => {
    if (existingTag && selectedTagRegexesSet) {
      const tag = tagRegexes.find((t) => t.id === existingTag);
      if (tag) {
        const keywords = extractKeywordsFromJsonRules(tag.jsonRules || "");
        dispatch(
          addNewTag(
            selectedTagRegexesSet.name,
            getTagName(existingTag),
            getTagLabel(existingTag).replace("(", "").replace(")", ""),
            tag.regexes,
            {
              keywords: keywords.positiveKeywords,
              negativeKeywords: keywords.negativeKeywords,
              tag: getTagName(existingTag),
            },
          ),
        );
        setChangesMade(true);
      }
    }
  };

  const filteredTags = tagRegexes.filter(
    (t) =>
      selectedTagIds.includes(t.id) &&
      (t.tag.toLowerCase().includes(filterText.toLowerCase()) ||
        t?.tagLabel?.toLowerCase().includes(filterText.toLowerCase())),
  );

  const history = useHistory();
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedAccountReconciler } = useSelector(
    (state: ReduxState) => state.Reconciler,
  );
  const toggleAddNewCategoryGroup = () => setShowNewGroup(!showNewGroup);

  const handleCategoryGroupChange = (value: any) => {
    if (!value) return;

    confirmAlert({
      customUI: getCustomUi({
        onConfirm: async () => {
          try {
            // Reset all edited categories with deleteCategory=true
            if (l1 && l2) {
              await resetEditedCategoriesTagLevels(
                l1,
                l2,
                selectedAccountReconciler || "",
                true,
              );
            } else if (job && job.id) {
              await resetEditedCategories(
                job.id,
                selectedAccountReconciler || "",
                true,
              );
            }

            // Change category group
            dispatch(setSelectedTagRegexesSet(value.value));
            localStorage.setItem(lsKey, value.value);

            // Re-categorize with new category group
            dispatch(
              createAlert({
                message:
                  "Category group changed. Re-categorizing transactions...",
                timeout: 3000,
                type: "success",
              }),
            );

            // Auto-trigger categorization with the new category group
            setTimeout(() => {
              dispatch(categorizeActionAsync({ l1, l2 }));
            }, 500);
          } catch (error: any) {
            dispatch(
              createAlert({
                message: error.message || "Error resetting categories. Please try again.",
                timeout: 0,
                type: "error",
              }),
            );
          }
        },
        confirmCTA: "Switch and Re-categorize",
        cancelCTA: "Cancel",
        getMessage: () => (
          <div style={{ marginBottom: "20px" }}>
            <h5>Switch to "{value.label}"?</h5>
            <p>
              This document will be re-categorized using the rules from "{value.label}".
              Manual category edits you've made on this document will be cleared.
            </p>
            <p className="text-muted small mb-0">
              Your other category groups and their rules are not affected. You can
              switch back any time.
            </p>
          </div>
        ),
      }),
    });
  };

  React.useEffect(() => {
    if (showNewGroup) {
      setGroupName("");
    }
  }, [showNewGroup]);

  const performAddNewCategoryGroup = () => {
    const rule: TagCategoryRule = {
      keywords: [],
      negativeKeywords: [],
      tag: groupName.trim(),
    };
    dispatch(addNewCategoryGroup(groupName, rule));
    localStorage.setItem(lsKey, groupName);
    toggleAddNewCategoryGroup();
  };

  const handleAddNewCategoryGroup = () => {
    const trimmed = groupName.trim();
    const hasExistingGroups = names.length > 0;
    const currentName = selectedTagRegexesSet?.name
      ? hideStr(selectedTagRegexesSet.name)
      : "";

    if (!hasExistingGroups) {
      performAddNewCategoryGroup();
      return;
    }

    confirmAlert({
      customUI: getCustomUi({
        onConfirm: performAddNewCategoryGroup,
        confirmCTA: "Create Empty Group",
        cancelCTA: "Cancel",
        getMessage: () => (
          <div style={{ marginBottom: "20px" }}>
            <h5>Create empty group "{trimmed}"?</h5>
            <p>
              The new group starts with no rules, so transactions will show as
              Uncategorized until you add categories to it.
            </p>
            {currentName ? (
              <p className="text-muted small mb-0">
                Your current group ("{currentName}") and its rules will not be changed.
              </p>
            ) : null}
          </div>
        ),
      }),
    });
  };

  return (
    <div>
      <AlertBanner />
      <Row className="align-items-center">
        {names.length > 1 ? (
          <Col xs="auto" className="d-flex align-items-center pr-2">
            <small className="text-muted mr-2 text-nowrap">Group:</small>
            <div style={{ minWidth: 180 }}>
              <Select
                className="categorization-tour-select-group"
                data-cy="main-category-select"
                options={names.map((x) => ({ label: hideStr(x), value: x }))}
                value={
                  selectedTagRegexesSet
                    ? {
                        label: hideStr(selectedTagRegexesSet.name),
                        value: selectedTagRegexesSet.id as string,
                      }
                    : null
                }
                onChange={handleCategoryGroupChange}
              />
            </div>
            <Dropdown className="ml-1">
              <Dropdown.Toggle
                variant="link"
                id="group-actions-dropdown"
                style={{ color: "#6c757d", padding: "0 6px", boxShadow: "none" }}
              >
                <FontAwesomeIcon icon="ellipsis-v" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  className="categorization-tour-add-group"
                  onClick={toggleAddNewCategoryGroup}
                >
                  <FontAwesomeIcon icon="plus" className="mr-2" />
                  New Group
                </Dropdown.Item>
                {!isDefault && (
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => {
                      confirmAlert({
                        customUI: getCustomUi({
                          onConfirm: () => {
                            if (selectedTagRegexesSet) {
                              deleteTagRegexesSet(
                                selectedTagRegexesSet.name,
                                selectedTagRegexesSet.type,
                              )
                                .then(() => {
                                  dispatch(fetchTagRegexes("categorize"));
                                  if (tagRegexesSets.length > 0) {
                                    dispatch(
                                      setSelectedTagRegexesSet(tagRegexesSets[0].name),
                                    );
                                  } else {
                                    dispatch(setSelectedTagRegexesSet(""));
                                  }
                                })
                                .catch((err) =>
                                  dispatch(
                                    createAlert({
                                      message: err.message || "Error deleting. Please try again.",
                                      timeout: 0,
                                      type: "error",
                                    }),
                                  ),
                                );
                            }
                          },
                          confirmCTA: "Delete",
                          cancelCTA: "Cancel",
                          getMessage: () => (
                            <div
                              className="div--confirm-job-delete"
                              style={{ marginBottom: "20px" }}
                            >
                              <span>Are you sure you want to delete this category group?</span>
                            </div>
                          ),
                        }),
                      });
                    }}
                  >
                    <FontAwesomeIcon icon="trash" className="mr-2" />
                    Delete Group
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        ) : (
          <Col xs="auto" className="pr-2">
            <Button
              className="categorization-tour-add-group"
              variant="link"
              size="sm"
              onClick={toggleAddNewCategoryGroup}
              style={{ color: "#6c757d", fontSize: "0.8rem" }}
            >
              <FontAwesomeIcon icon="plus" />
              <span className="ml-1">New group</span>
            </Button>
          </Col>
        )}

        <Button
          className="categorization-tour-edit-keywords"
          variant="outline-primary"
          onClick={toggle}
          data-cy="edit-categories-btn"
        >
          <FontAwesomeIcon icon="edit" className="mr-2" />
          Edit Categories
        </Button>

        {/* Edit Categories modal */}
        <Modal show={show} onHide={toggle} size="xl" dialogClassName="modal-95w">
          {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
          <Modal.Header closeButton>
            <Modal.Title>Edit Categories</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "1.5rem", maxWidth: "100%", width: "100%" }}>
            <div className="mb-3">
              <AlertBanner />
            </div>
            <div className="mb-3">
              <div className="mb-3">
                <InputGroup style={{ maxWidth: "400px" }}>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon="search" className="text-muted" />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <FormControl
                    type="text"
                    placeholder="Search categories by name..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  {filterText && (
                    <InputGroup.Append>
                      <Button
                        variant="outline-secondary"
                        onClick={() => setFilterText("")}
                      >
                        <FontAwesomeIcon icon="times" />
                      </Button>
                    </InputGroup.Append>
                  )}
                </InputGroup>
                <div className="text-muted small mt-1">
                  {filteredTags.length}{" "}
                  {filteredTags.length === 1 ? "category" : "categories"}
                </div>
              </div>
              <div>
                <Button
                  variant="link"
                  onClick={() => setShowAddNewRow(true)}
                  disabled={showAddNewRow}
                >
                  <FontAwesomeIcon icon="plus" />
                  <span className="ml-2">Add New Category</span>
                </Button>
              </div>
            </div>

            <CategoryTable
              tags={
                filteredTags.sort((a, b) =>
                  a.tag.localeCompare(b.tag),
                ) as unknown as {
                  id: string;
                  tag: string;
                  tagLabel?: string;
                  regexes: string;
                  jsonRules: TagCategoryRule;
                }[]
              }
              systemKeywords={systemKeywords}
              onDeleteTag={(id) => onDeleteTag(id)}
              onAddKeyword={(tagId, keyword, isPositive) =>
                onAddNewKeyword(tagId, keyword, isPositive)
              }
              onRemoveKeyword={onRemoveKeyword}
              onSaveTag={onSaveTag}
              onAddNewCategory={handleAddCategory}
              showAddNewRow={showAddNewRow}
              onCancelAddNew={() => setShowAddNewRow(false)}
            />
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              variant="outline-secondary"
              onClick={() => {
                toggle();
                const params = new URLSearchParams();
                if (l1 !== undefined && l1 !== null) params.set("l1", l1);
                if (l2 !== undefined && l2 !== null) params.set("l2", l2);
                if (selectedTagRegexesSet?.name)
                  params.set("categoryGroup", selectedTagRegexesSet.name);
                history.push(`/extract/vendorscsv?${params.toString()}`);
              }}
            >
              <FontAwesomeIcon icon="upload" className="mr-2" />
              Upload CSV
            </Button>
            <Button variant="secondary" onClick={toggle}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add New Group modal */}
        <Modal show={showNewGroup} onHide={toggleAddNewCategoryGroup}>
          {/* @ts-ignore - react-bootstrap 1.6.1 type definition issue */}
          <Modal.Header closeButton>
            <Modal.Title>Add New Category Group</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Label>Category Group Name</Form.Label>
                  <Form.Control
                    name="groupName"
                    value={groupName}
                    onChange={(e) => {
                      setGroupName(e.target.value);
                    }}
                    type="text"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={toggleAddNewCategoryGroup}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleAddNewCategoryGroup}
            >
              <FontAwesomeIcon icon="plus" />
              <span className="ml-1" data-cy="add-new-category-group-modal-button">
                Add New Category Group
              </span>
            </Button>
          </Modal.Footer>
        </Modal>
      </Row>
    </div>
  );
};
export default TagRegexes;
